/**
 * useGlobalDragDrop - Window-level drag detection for file uploads
 *
 * This hook listens for drag events on the window and provides state
 * for showing a global drop overlay when files are dragged over the browser.
 */

import { useState, useCallback, useEffect, useRef } from "react";

interface UseGlobalDragDropOptions {
  /** Called when files are dropped */
  onDrop: (files: File[]) => void;
  /** Whether to enable the drag detection (default: true) */
  enabled?: boolean;
}

interface UseGlobalDragDropReturn {
  /** Whether files are currently being dragged over the window */
  isDragging: boolean;
  /** Reset the drag state (call when drop is handled elsewhere) */
  resetDragState: () => void;
}

/**
 * Hook for detecting file drag operations at the window level.
 * Shows a global overlay when files are dragged from outside the browser.
 */
export function useGlobalDragDrop({
  onDrop,
  enabled = true,
}: UseGlobalDragDropOptions): UseGlobalDragDropReturn {
  const [isDragging, setIsDragging] = useState(false);
  // Counter to handle nested drag enter/leave events
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!enabled) return;

      // Check if this is a file drag (vs text selection, etc.)
      if (e.dataTransfer?.types.includes("Files")) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) {
          setIsDragging(true);
        }
      }
    },
    [enabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!enabled) return;

      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    },
    [enabled],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Required to allow drop
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset drag state
      dragCounterRef.current = 0;
      setIsDragging(false);

      if (!enabled) return;

      // Get dropped files
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        onDrop(Array.from(files));
      }
    },
    [enabled, onDrop],
  );

  useEffect(() => {
    if (!enabled) {
      setIsDragging(false);
      dragCounterRef.current = 0;
      return;
    }

    // Add window-level listeners
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, [enabled, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  // Reset function for when drop is handled by overlay
  const resetDragState = useCallback(() => {
    dragCounterRef.current = 0;
    setIsDragging(false);
  }, []);

  return { isDragging, resetDragState };
}
